import { Outlet } from "react-router-dom";

import { Navigate } from "@/components/navigation-controls/navigate";
import { NotFoundView } from "@/components/not-found-view";
import { authRoutes } from "@/domain/_shared/auth/routes/auth.routes.tsx";
import { fileRoutes } from "@/domain/_shared/file/routes/file.routes.tsx";
import { climateActionRoutes, myClimateActionRoutes } from "@/domain/climate-action/routes/climate-action.routes.tsx";
import { focusAreaRoutes, focusAreaSettingsRoutes } from "@/domain/focus-area/routes/focus-area.routes.tsx";
import { indicatorRoutes } from "@/domain/indicator/routes/indicator.routes.tsx";
import { labelSettingsRoutes } from "@/domain/label/routes/label.routes.tsx";
import { phaseSettingsRoutes } from "@/domain/phase/routes/phase.routes.tsx";
import { prioritySettingsRoutes } from "@/domain/priority/routes/priority.routes.tsx";
import { myProjectRoutes } from "@/domain/project/routes/project-routes.tsx";
import { myTasksRoutes } from "@/domain/task/routes/task.routes.tsx";
import { teamRoutes, teamSettingsRoutes } from "@/domain/team/routes/team.routes.tsx";
import { userSettingsRoutes } from "@/domain/user/routes/user.routes.tsx";
import { workspaceAdminRoutes, workspaceSettingsRoutes } from "@/domain/workspace/routes/workspace.routes.tsx";

import { unitSettingsRoutes } from "../domain/unit/routes/unit.routes.tsx";

import { AppLayout } from "./app.layout.tsx";
import { ProtectedRoute } from "./protected-route.tsx";
import { SettingsRootLayout } from "./settings.layout.tsx";
import { WorkspaceLayout } from "./workspace.layout.tsx";

export const routes = (isAuthenticated: boolean) => [
  {
    path: "/",
    element: (
      // if we directly navigate to /, redirect to the first workspace of login depending on authentication state
      <ProtectedRoute isAuthenticated={isAuthenticated}>
        <AppLayout />
      </ProtectedRoute>
    ),
  },

  // ---------------------- PUBLIC ROUTES ----------------------------

  ...authRoutes(isAuthenticated),
  ...fileRoutes,

  // -------------------- MAIN APPLICATION ROUTES --------------------
  {
    path: "/:workspaceUrl",
    element: (
      <ProtectedRoute isAuthenticated={isAuthenticated}>
        <WorkspaceLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: "", element: <Navigate to="my-climate-actions" replace /> },

      // Personalized User Routes
      ...myTasksRoutes,
      ...myProjectRoutes,
      ...myClimateActionRoutes,

      // Workspace Related Routes
      ...climateActionRoutes,
      ...focusAreaRoutes,
      ...indicatorRoutes,

      // Team Related Routes
      ...teamRoutes,

      // Fall Through
      { path: "*", element: <NotFoundView /> },
    ],
  },

  // -------------------- SETTINGS ROUTES --------------------
  {
    path: "/:workspaceUrl/settings",
    element: (
      <ProtectedRoute isAuthenticated={isAuthenticated}>
        <SettingsRootLayout />
      </ProtectedRoute>
    ),
    children: [
      ...userSettingsRoutes,

      {
        path: "workspace",
        element: <Outlet />,
        children: [
          ...workspaceSettingsRoutes,
          ...focusAreaSettingsRoutes,
          ...labelSettingsRoutes,
          ...phaseSettingsRoutes,
          ...prioritySettingsRoutes,
          ...unitSettingsRoutes,
        ],
      },

      ...teamSettingsRoutes,

      { path: "*", element: <NotFoundView /> },
    ],
  },

  // -------------------- SUPERUSER ROUTES --------------------
  ...workspaceAdminRoutes(isAuthenticated),

  { path: "*", element: <NotFoundView /> },
];
